"use client";

import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";

import cn from "classnames";

import { ChevronDownIcon } from "@/components/icons/chevronDown.icon";

const focusRingClassName =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-primary";

export const Dropdown = ({
  id,
  name,
  value,
  options,
  placeholder = "Select option",
  onChange,
  disabled,
  invalid,
  required,
  labelledBy,
  describedBy,
  className,
}: DropdownProps) => {
  const generatedId = useId();
  const triggerId = id || generatedId;
  const valueId = `${triggerId}-value`;
  const listId = `${triggerId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedOption =
    selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const selectedLabel = selectedOption?.label ?? placeholder;

  const openList = (index: number) => {
    if (options.length === 0) return;
    const next = index < 0 ? 0 : Math.min(index, options.length - 1);
    setActiveIndex(next);
    setOpen(true);
  };

  const closeList = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const moveActive = (index: number) => {
    if (options.length === 0) return;
    setActiveIndex(Math.min(Math.max(index, 0), options.length - 1));
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      closeList();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (open) moveActive(activeIndex + 1);
      else openList(selectedIndex);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (open) moveActive(activeIndex - 1);
      else openList(selectedIndex >= 0 ? selectedIndex : options.length - 1);
    }
  };

  const onOptionKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(index + 1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(index - 1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      moveActive(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      moveActive(options.length - 1);
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onChange(options[index].value);
      closeList(true);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeList(true);
      return;
    }

    if (event.key === "Tab") setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn("relative min-h-14 min-w-0", className)}>
      <input type="hidden" name={name} value={value} />
      <div
        className={cn(
          "bg-white",
          open
            ? "border-greyscale-strength absolute top-0 left-0 z-20 flex w-max min-w-full flex-col overflow-hidden rounded-[24px] border shadow-[0_12px_28px_rgba(0,0,0,0.16)]"
            : "rounded-full",
        )}>
        <button
          ref={triggerRef}
          type="button"
          id={triggerId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-labelledby={labelledBy ? `${labelledBy} ${valueId}` : undefined}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          onClick={() => {
            if (open) closeList();
            else openList(selectedIndex);
          }}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "text-greyscale-headline flex min-h-14 w-full items-center justify-between gap-3 px-5 text-left text-[18px] transition-colors",
            open
              ? focusRingClassName
              : cn(
                  "rounded-full border bg-white outline-none",
                  invalid
                    ? "border-error focus:border-error focus:ring-error focus:ring-1"
                    : "border-greyscale-strength focus:border-secondary focus:ring-secondary focus:ring-1",
                ),
          )}>
          <span id={valueId} className="min-w-0 truncate">
            {selectedLabel}
          </span>
          <ChevronDownIcon
            width={20}
            height={20}
            aria-hidden
            title=""
            className={cn(
              "shrink-0 transition-transform motion-reduce:transition-none",
              open && "rotate-180",
            )}
          />
        </button>
        {open ? (
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={labelledBy ?? triggerId}
            className="pb-2">
            {options.map((option, index) => {
              const selected = option.value === value;
              return (
                <li key={option.value} role="presentation">
                  <button
                    ref={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={selected}
                    className={cn(
                      "text-greyscale-headline focus-visible:outline-primary w-full px-5 py-2.5 text-left text-[18px] whitespace-nowrap hover:bg-black/5 focus-visible:bg-black/5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid",
                      selected && "font-semibold",
                    )}
                    onClick={() => {
                      onChange(option.value);
                      closeList(true);
                    }}
                    onKeyDown={(event) => onOptionKeyDown(event, index)}>
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export type DropdownOption = {
  value: string;
  label: string;
};

export type DropdownProps = {
  id?: string;
  name: string;
  value: string;
  options: readonly DropdownOption[];
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  labelledBy?: string;
  describedBy?: string;
  className?: string;
};
